/**
 * Colour tokens for the ZERO content area (everything rendered inside the
 * shell tile). The shell — sidebar, app bar, account menus — keeps the inline
 * theme in `App.tsx` and never sees these tokens.
 *
 * ZERO purple is the only accent. There is no green anywhere: positive states
 * use the accent or ink, warnings use amber / orange, failures use red
 * (`contentColors.test.ts` rejects any token with a hue between 75° and 175°).
 */

export type AppColorMode = 'light' | 'dark';

export const CONTENT_PALETTE = {
  light: {
    primary: { main: '#7C3AED', light: '#A78BFA', dark: '#5B21B6', contrastText: '#FFFFFF' },
    secondary: { main: '#334155', light: '#475569', dark: '#0F172A', contrastText: '#FFFFFF' },
    success: { main: '#6D28D9', light: '#8B5CF6', dark: '#5B21B6', contrastText: '#FFFFFF' },
    warning: { main: '#B45309', light: '#F59E0B', dark: '#92400E', contrastText: '#FFFFFF' },
    error: { main: '#C62828', light: '#D64545', dark: '#9B1C1C', contrastText: '#FFFFFF' },
    info: { main: '#6D28D9', light: '#8B5CF6', dark: '#5B21B6', contrastText: '#FFFFFF' },
    text: { primary: '#0F172A', secondary: '#475569', disabled: '#64748B' },
    background: {
      default: '#F1F5F9',
      paper: '#FFFFFF',
      elevated: '#F8FAFC',
      input: '#FFFFFF',
      inputFocused: '#FFFFFF',
      shell: '#FFFFFF',
    },
  },
  dark: {
    primary: { main: '#A78BFA', light: '#C4B5FD', dark: '#8B5CF6', contrastText: '#0F172A' },
    secondary: { main: '#CBD5E1', light: '#E2E8F0', dark: '#94A3B8', contrastText: '#0F172A' },
    success: { main: '#C4B5FD', light: '#DDD6FE', dark: '#A78BFA', contrastText: '#0F172A' },
    warning: { main: '#F0B45A', light: '#F6CD8E', dark: '#C98A2E', contrastText: '#0F172A' },
    error: { main: '#F87171', light: '#FCA5A5', dark: '#DC2626', contrastText: '#0F172A' },
    info: { main: '#C4B5FD', light: '#DDD6FE', dark: '#A78BFA', contrastText: '#0F172A' },
    text: { primary: '#F8FAFC', secondary: '#CBD5E1', disabled: '#94A3B8' },
    background: {
      default: '#0F172A',
      paper: '#1E293B',
      elevated: '#273449',
      input: '#1E293B',
      inputFocused: '#273449',
      shell: '#1E293B',
    },
  },
} as const;

export const CONTENT_BORDERS = {
  light: {
    subtle: 'rgba(15, 23, 42, 0.08)',
    default: 'rgba(15, 23, 42, 0.14)',
    strong: 'rgba(15, 23, 42, 0.28)',
    rule: '#0F172A',
  },
  dark: {
    subtle: 'rgba(255, 255, 255, 0.08)',
    default: 'rgba(255, 255, 255, 0.14)',
    strong: 'rgba(255, 255, 255, 0.26)',
    rule: '#F8FAFC',
  },
} as const;

export const CONTENT_ACTION = {
  light: {
    hover: 'rgba(15, 23, 42, 0.04)',
    selected: 'rgba(124, 58, 237, 0.08)',
    focusRing: 'rgba(124, 58, 237, 0.24)',
  },
  dark: {
    hover: 'rgba(255, 255, 255, 0.06)',
    selected: 'rgba(167, 139, 250, 0.18)',
    focusRing: 'rgba(167, 139, 250, 0.32)',
  },
} as const;

/**
 * Muted, mutually distinct series colours with no greens (purple, ochre,
 * steel, rust, clay, slate-blue, charcoal, rose). The first series is purple.
 * Composition charts use `accentRamp` (one hue) instead.
 */
export const CONTENT_CHART_COLORS = {
  light: ['#7C3AED', '#B07A1E', '#3D5A80', '#A8442A', '#8C6D5A', '#7A869A', '#334155', '#BE185D'],
  dark: ['#A78BFA', '#E0A04A', '#8FB0D6', '#F07A7A', '#C4A48E', '#A3AEC2', '#CBD5E1', '#F472B6'],
} as const;

/** Architectural radius scale (px) for the content area. */
export const CONTENT_SHAPE = {
  /** chips, tooltips, skeletons, bars */
  xs: 4,
  /** buttons, inputs, alerts, icon squares */
  sm: 6,
  /** panels, cards, dialogs, map frames */
  md: 8,
} as const;
